import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mic, MicOff } from 'lucide-react';
import { generateRoomId } from '@speech-translator/shared';
import { API_CONFIG } from '@/config/api';

export function SimpleSpeakerRoom() {
  const [roomId, setRoomId] = useState<string>('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState<string>('');
  const [error, setError] = useState<string>('');
  
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const createRoom = async () => {
    const newRoomId = generateRoomId();
    setRoomId(newRoomId);
    
    // Connect WebSocket
    const wsUrl = `${API_CONFIG.WEBSOCKET_URL}/ws/${newRoomId}?role=speaker`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      ws.send(JSON.stringify({
        type: 'join_room',
        timestamp: Date.now(),
        data: { roomId: newRoomId, role: 'speaker' }
      }));
    };
    
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === 'transcription') {
        setTranscription(prev => prev + ' ' + message.data.text);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('WebSocket connection failed');
    };
  };

  const startRecording = async () => {
    try {
      // Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true } 
      });
      streamRef.current = stream;
      
      // Create audio context
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      
      // Create nodes
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      // Connect nodes
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Process audio
      processor.onaudioprocess = (event) => {
        if (!isRecording || !wsRef.current) return;
        
        const inputData = event.inputBuffer.getChannelData(0);
        
        // Convert to 16-bit PCM
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
        }
        
        // Convert to base64
        const uint8Array = new Uint8Array(pcmData.buffer);
        const base64Data = btoa(String.fromCharCode(...uint8Array));
        
        // Send via WebSocket
        wsRef.current.send(JSON.stringify({
          type: 'audio_data',
          timestamp: Date.now(),
          data: {
            audioData: base64Data,
            sequence: Date.now()
          }
        }));
      };
      
      setIsRecording(true);
      setError('');
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    if (processorRef.current) {
      processorRef.current.disconnect();
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold text-center">Simple Speaker Room</h1>
      
      {!roomId ? (
        <Card>
          <CardHeader>
            <CardTitle>Create Room</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={createRoom} className="w-full">
              Create Room
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Room: {roomId}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <Button
                  onClick={toggleRecording}
                  variant={isRecording ? "destructive" : "default"}
                  size="lg"
                  className="h-16 w-16 rounded-full"
                >
                  {isRecording ? <MicOff className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
                </Button>
              </div>
              
              {error && (
                <div className="p-3 bg-red-100 border border-red-300 rounded text-red-700">
                  {error}
                </div>
              )}
              
              <div className="p-4 bg-gray-100 rounded min-h-[100px]">
                <h3 className="font-semibold mb-2">Transcription:</h3>
                <p>{transcription || 'Start speaking to see transcription...'}</p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}